import type {
  gasEstimateForUnprovenUnshield,
  generateUnshieldProof,
  populateProvedUnshield,
  gasEstimateForUnprovenUnshieldBaseToken,
  generateUnshieldBaseTokenProof,
  populateProvedUnshieldBaseToken
} from '@railgun-community/wallet'
import { loadRailgunSdk } from '@web/utils/railgunSdk'
import {
  calculateGasPrice,
  NetworkName,
  TXIDVersion,
  type RailgunERC20Amount,
  type RailgunERC20AmountRecipient,
  type TransactionGasDetails
} from '@railgun-community/shared-models'
import { JsonRpcProvider } from 'ethers'

import { getWrappedBaseTokenAddress } from './railgunTokens'
import { getNetworkNameForChainId, getRpcUrlForChainId, RAILGUN_DEBUG } from './railgunCore'
import { derivePublicSigner } from './railgunMnemonic'
import {
  getErrorMessage,
  getGasDetailsForTransaction,
  computeGasFeeNativeAndUsd,
  computeGasFeeOptionsNativeAndUsd,
  isProvingMerkleFailure,
  triggerFullUTXORescanForChain,
  warmMainEngineForChain,
  beginMerkletreeQuiescenceForChain,
  type RailgunGasFeeOption,
  type RailgunGasSpeedName
} from './railgunShared'
import { FEATURES } from '@web/constants/features'
import { sdkInvokeInOffscreen } from '@web/extension-services/background/offscreen/railgunOffscreenClient'

function wrapUnshieldError(err: unknown): never {
  const message = getErrorMessage('unshield', err)
  throw err instanceof Error && message === err.message ? err : new Error(message, { cause: err })
}

// Engine in-process (flag off) vs offscreen (flag on). Same routing pattern as railgunTransfer.
// The progress-callback functions never serialize over structured-clone; we replace them with
// `null` on the wire and the offscreen registry splices a real callback in from `progressId`.

async function invokeGasEstimateForUnprovenUnshield(
  args: Parameters<typeof gasEstimateForUnprovenUnshield>
): Promise<Awaited<ReturnType<typeof gasEstimateForUnprovenUnshield>>> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    return sdkInvokeInOffscreen({ method: 'gasEstimateForUnprovenUnshield', args })
  }
  const { gasEstimateForUnprovenUnshield } = await loadRailgunSdk()
  return gasEstimateForUnprovenUnshield(...args)
}

async function invokeGenerateUnshieldProof(
  args: Parameters<typeof generateUnshieldProof>,
  onProgress?: (progress: number) => void
): Promise<void> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    const wireArgs = [...args]
    wireArgs[wireArgs.length - 1] = null
    await sdkInvokeInOffscreen({ method: 'generateUnshieldProof', args: wireArgs, onProgress })
    return
  }
  const { generateUnshieldProof } = await loadRailgunSdk()
  await generateUnshieldProof(...args)
}

async function invokePopulateProvedUnshield(
  args: Parameters<typeof populateProvedUnshield>
): Promise<Awaited<ReturnType<typeof populateProvedUnshield>>> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    return sdkInvokeInOffscreen({ method: 'populateProvedUnshield', args })
  }
  const { populateProvedUnshield } = await loadRailgunSdk()
  return populateProvedUnshield(...args)
}

async function invokeGasEstimateForUnprovenUnshieldBaseToken(
  args: Parameters<typeof gasEstimateForUnprovenUnshieldBaseToken>
): Promise<Awaited<ReturnType<typeof gasEstimateForUnprovenUnshieldBaseToken>>> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    return sdkInvokeInOffscreen({ method: 'gasEstimateForUnprovenUnshieldBaseToken', args })
  }
  const { gasEstimateForUnprovenUnshieldBaseToken } = await loadRailgunSdk()
  return gasEstimateForUnprovenUnshieldBaseToken(...args)
}

async function invokeGenerateUnshieldBaseTokenProof(
  args: Parameters<typeof generateUnshieldBaseTokenProof>,
  onProgress?: (progress: number) => void
): Promise<void> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    const wireArgs = [...args]
    wireArgs[wireArgs.length - 1] = null
    await sdkInvokeInOffscreen({
      method: 'generateUnshieldBaseTokenProof',
      args: wireArgs,
      onProgress
    })
    return
  }
  const { generateUnshieldBaseTokenProof } = await loadRailgunSdk()
  await generateUnshieldBaseTokenProof(...args)
}

async function invokePopulateProvedUnshieldBaseToken(
  args: Parameters<typeof populateProvedUnshieldBaseToken>
): Promise<Awaited<ReturnType<typeof populateProvedUnshieldBaseToken>>> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    return sdkInvokeInOffscreen({ method: 'populateProvedUnshieldBaseToken', args })
  }
  const { populateProvedUnshieldBaseToken } = await loadRailgunSdk()
  return populateProvedUnshieldBaseToken(...args)
}

export type UnshieldGasEstimation = {
  gasFeeAmountNative: string
  gasFeeUsd?: string
  feeOptions?: RailgunGasFeeOption[]
}

export type PreparedUnshieldGasDetails = {
  transactionGasDetails: TransactionGasDetails
  overallBatchMinGasPrice: Awaited<ReturnType<typeof calculateGasPrice>>
}

export async function estimateUnshieldGasERC20(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  amount: bigint
  tokenAddress: string
  railgunWalletID: string
  encryptionKey: string
  recipientAddress: string
}): Promise<UnshieldGasEstimation> {
  const {
    mnemonic,
    chainId,
    amount,
    tokenAddress,
    railgunWalletID,
    encryptionKey,
    recipientAddress
  } = params

  const networkName = getNetworkNameForChainId(chainId)
  if (!networkName) {
    throw new Error(`Unsupported chainId for Railgun unshield: ${chainId}`)
  }

  const rpcUrl = getRpcUrlForChainId(chainId)
  if (!rpcUrl) {
    throw new Error(`No RPC URL configured for chainId ${chainId}`)
  }

  const provider = new JsonRpcProvider(rpcUrl)
  const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

  const sendWithPublicWallet = true

  const erc20AmountRecipients: RailgunERC20AmountRecipient[] = [
    {
      tokenAddress,
      amount,
      recipientAddress
    }
  ]

  const originalGasDetails = await getGasDetailsForTransaction(
    networkName,
    0n,
    sendWithPublicWallet,
    wallet,
    chainId
  )
  const feeTokenDetails = undefined

  await warmMainEngineForChain(chainId, railgunWalletID)

  const { gasEstimate } = await invokeGasEstimateForUnprovenUnshield([
    TXIDVersion.V2_PoseidonMerkle,
    networkName,
    railgunWalletID,
    encryptionKey,
    erc20AmountRecipients,
    [], // nft amount recipients
    originalGasDetails,
    feeTokenDetails,
    sendWithPublicWallet
  ])

  const gasDetails = await getGasDetailsForTransaction(
    networkName,
    gasEstimate,
    sendWithPublicWallet,
    wallet,
    chainId
  )
  const feeSummary = await computeGasFeeNativeAndUsd({ gasEstimate, gasDetails, chainId })
  const feeOptions = await computeGasFeeOptionsNativeAndUsd({ gasEstimate, chainId, wallet })
  return { ...feeSummary, feeOptions }
}

export async function estimateUnshieldGasBaseToken(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  amount: bigint
  railgunWalletID: string
  encryptionKey: string
  recipientAddress: string
}): Promise<UnshieldGasEstimation> {
  const { mnemonic, chainId, amount, railgunWalletID, encryptionKey, recipientAddress } = params

  const networkName = getNetworkNameForChainId(chainId)
  if (!networkName) {
    throw new Error(`Unsupported chainId for Railgun unshield: ${chainId}`)
  }

  const rpcUrl = getRpcUrlForChainId(chainId)
  if (!rpcUrl) {
    throw new Error(`No RPC URL configured for chainId ${chainId}`)
  }

  const wrappedTokenAddress = getWrappedBaseTokenAddress(chainId)
  if (!wrappedTokenAddress) {
    throw new Error(`No wrapped base token configured for chainId ${chainId}`)
  }

  const provider = new JsonRpcProvider(rpcUrl)
  const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

  const sendWithPublicWallet = true

  const wrappedERC20Amount: RailgunERC20Amount = {
    tokenAddress: wrappedTokenAddress,
    amount
  }

  const originalGasDetails = await getGasDetailsForTransaction(
    networkName,
    0n,
    sendWithPublicWallet,
    wallet,
    chainId
  )
  const feeTokenDetails = undefined

  await warmMainEngineForChain(chainId, railgunWalletID)

  const { gasEstimate } = await invokeGasEstimateForUnprovenUnshieldBaseToken([
    TXIDVersion.V2_PoseidonMerkle,
    networkName,
    recipientAddress,
    railgunWalletID,
    encryptionKey,
    wrappedERC20Amount,
    originalGasDetails,
    feeTokenDetails,
    sendWithPublicWallet
  ])

  const gasDetails = await getGasDetailsForTransaction(
    networkName,
    gasEstimate,
    sendWithPublicWallet,
    wallet,
    chainId
  )
  const feeSummary = await computeGasFeeNativeAndUsd({ gasEstimate, gasDetails, chainId })
  const feeOptions = await computeGasFeeOptionsNativeAndUsd({ gasEstimate, chainId, wallet })
  return { ...feeSummary, feeOptions }
}

export async function generateUnshieldProofERC20WithSDK(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  amount: bigint
  tokenAddress: string
  railgunWalletID: string
  encryptionKey: string
  recipientAddress: string
  feeSpeed?: RailgunGasSpeedName
}): Promise<PreparedUnshieldGasDetails> {
  const {
    mnemonic,
    chainId,
    amount,
    tokenAddress,
    railgunWalletID,
    encryptionKey,
    recipientAddress,
    feeSpeed
  } = params

  const networkName = getNetworkNameForChainId(chainId)
  if (!networkName) {
    throw new Error(`Unsupported chainId for Railgun unshield: ${chainId}`)
  }

  const rpcUrl = getRpcUrlForChainId(chainId)
  if (!rpcUrl) {
    throw new Error(`No RPC URL configured for chainId ${chainId}`)
  }

  const provider = new JsonRpcProvider(rpcUrl)
  const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

  const sendWithPublicWallet = true

  const erc20AmountRecipients: RailgunERC20AmountRecipient[] = [
    {
      tokenAddress,
      amount,
      recipientAddress
    }
  ]

  const originalGasDetails = await getGasDetailsForTransaction(
    networkName,
    0n,
    sendWithPublicWallet,
    wallet,
    chainId
  )
  const feeTokenDetails = undefined

  let releaseMerkletree = await beginMerkletreeQuiescenceForChain(chainId, railgunWalletID)
  let transactionGasDetails: TransactionGasDetails
  let overallBatchMinGasPrice: Awaited<ReturnType<typeof calculateGasPrice>>

  try {
    const { gasEstimate } = await invokeGasEstimateForUnprovenUnshield([
      TXIDVersion.V2_PoseidonMerkle,
      networkName,
      railgunWalletID,
      encryptionKey,
      erc20AmountRecipients,
      [], // nft amount recipients
      originalGasDetails,
      feeTokenDetails,
      sendWithPublicWallet
    ])

    transactionGasDetails = await getGasDetailsForTransaction(
      networkName,
      gasEstimate,
      sendWithPublicWallet,
      wallet,
      chainId,
      feeSpeed
    )

    overallBatchMinGasPrice = await calculateGasPrice(transactionGasDetails)

    const broadcasterFeeERC20AmountRecipient: RailgunERC20AmountRecipient | undefined = undefined

    const progressCallback = (progress: number) => {
      if (RAILGUN_DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[Railgun unshield] proof progress', progress)
      }
    }

    await invokeGenerateUnshieldProof(
      [
        TXIDVersion.V2_PoseidonMerkle,
        networkName,
        railgunWalletID,
        encryptionKey,
        erc20AmountRecipients,
        [], // nft amount recipients
        broadcasterFeeERC20AmountRecipient,
        sendWithPublicWallet,
        overallBatchMinGasPrice,
        progressCallback
      ],
      progressCallback
    )
  } catch (err) {
    // Proof failed against a stale UTXO merkletree — rebuild it in the background
    // so the user's retry can prove (the mapped message tells them to wait+retry).
    if (isProvingMerkleFailure(err)) {
      // Unfreeze first: the rescan runs its own interlock and needs to mutate.
      releaseMerkletree()
      releaseMerkletree = () => {}
      void triggerFullUTXORescanForChain(chainId, railgunWalletID)
    }
    wrapUnshieldError(err)
  } finally {
    releaseMerkletree()
  }

  return {
    transactionGasDetails,
    overallBatchMinGasPrice
  }
}

export async function executePreparedUnshieldERC20WithSDK(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  amount: bigint
  tokenAddress: string
  railgunWalletID: string
  encryptionKey: string
  recipientAddress: string
  gasDetails: PreparedUnshieldGasDetails
}): Promise<string> {
  const {
    mnemonic,
    chainId,
    amount,
    tokenAddress,
    railgunWalletID,
    encryptionKey,
    recipientAddress,
    gasDetails
  } = params

  const networkName = getNetworkNameForChainId(chainId)
  if (!networkName) {
    throw new Error(`Unsupported chainId for Railgun unshield: ${chainId}`)
  }

  const rpcUrl = getRpcUrlForChainId(chainId)
  if (!rpcUrl) {
    throw new Error(`No RPC URL configured for chainId ${chainId}`)
  }

  const provider = new JsonRpcProvider(rpcUrl)
  const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

  const sendWithPublicWallet = true

  const erc20AmountRecipients: RailgunERC20AmountRecipient[] = [
    {
      tokenAddress,
      amount,
      recipientAddress
    }
  ]

  const broadcasterFeeERC20AmountRecipient: RailgunERC20AmountRecipient | undefined = undefined

  const { transaction } = await invokePopulateProvedUnshield([
    TXIDVersion.V2_PoseidonMerkle,
    networkName,
    railgunWalletID,
    erc20AmountRecipients,
    [], // nftAmountRecipients
    broadcasterFeeERC20AmountRecipient,
    sendWithPublicWallet,
    gasDetails.overallBatchMinGasPrice,
    gasDetails.transactionGasDetails
  ])

  // Simulate transaction before broadcasting to catch invalid proofs early
  try {
    await provider.call({
      to: transaction.to,
      data: transaction.data,
      from: wallet.address,
      value: transaction.value ?? 0n
    })
  } catch (simErr) {
    // eslint-disable-next-line no-console
    console.error('[Railgun unshield] simulation failed:', simErr)
    throw new Error(
      `Unshield transaction would revert on-chain. This usually means the ZK proof is invalid ` +
        `(stale Merkle tree or corrupted circuit artifacts). Please try again. ` +
        `Details: ${simErr instanceof Error ? simErr.message : String(simErr)}`
    )
  }

  const tx = await wallet.sendTransaction(transaction)
  await tx.wait()
  return tx.hash
}

export async function unshieldERC20WithSDK(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  amount: bigint
  tokenAddress: string
  railgunWalletID: string
  encryptionKey: string
  recipientAddress: string
}): Promise<string> {
  const {
    mnemonic,
    chainId,
    amount,
    tokenAddress,
    railgunWalletID,
    encryptionKey,
    recipientAddress
  } = params

  try {
    const gasDetails = await generateUnshieldProofERC20WithSDK({
      mnemonic,
      passphrase: params.passphrase,
      chainId,
      amount,
      tokenAddress,
      railgunWalletID,
      encryptionKey,
      recipientAddress
    })

    return await executePreparedUnshieldERC20WithSDK({
      mnemonic,
      passphrase: params.passphrase,
      chainId,
      amount,
      tokenAddress,
      railgunWalletID,
      encryptionKey,
      recipientAddress,
      gasDetails
    })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('unshieldERC20WithSDK failed', err)
    wrapUnshieldError(err)
  }
}

export async function generateUnshieldProofBaseTokenWithSDK(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  amount: bigint
  railgunWalletID: string
  encryptionKey: string
  recipientAddress: string
  feeSpeed?: RailgunGasSpeedName
}): Promise<PreparedUnshieldGasDetails> {
  const { mnemonic, chainId, amount, railgunWalletID, encryptionKey, recipientAddress, feeSpeed } =
    params

  const networkName = getNetworkNameForChainId(chainId)
  if (!networkName) {
    throw new Error(`Unsupported chainId for Railgun unshield: ${chainId}`)
  }

  const rpcUrl = getRpcUrlForChainId(chainId)
  if (!rpcUrl) {
    throw new Error(`No RPC URL configured for chainId ${chainId}`)
  }

  const wrappedTokenAddress = getWrappedBaseTokenAddress(chainId)
  if (!wrappedTokenAddress) {
    throw new Error(`No wrapped base token configured for chainId ${chainId}`)
  }

  const provider = new JsonRpcProvider(rpcUrl)
  const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

  const sendWithPublicWallet = true

  const wrappedERC20Amount: RailgunERC20Amount = {
    tokenAddress: wrappedTokenAddress,
    amount
  }

  const originalGasDetails = await getGasDetailsForTransaction(
    networkName,
    0n,
    sendWithPublicWallet,
    wallet,
    chainId
  )
  const feeTokenDetails = undefined

  let releaseMerkletree = await beginMerkletreeQuiescenceForChain(chainId, railgunWalletID)
  let transactionGasDetails: TransactionGasDetails
  let overallBatchMinGasPrice: Awaited<ReturnType<typeof calculateGasPrice>>

  try {
    const { gasEstimate } = await invokeGasEstimateForUnprovenUnshieldBaseToken([
      TXIDVersion.V2_PoseidonMerkle,
      networkName,
      recipientAddress,
      railgunWalletID,
      encryptionKey,
      wrappedERC20Amount,
      originalGasDetails,
      feeTokenDetails,
      sendWithPublicWallet
    ])

    transactionGasDetails = await getGasDetailsForTransaction(
      networkName,
      gasEstimate,
      sendWithPublicWallet,
      wallet,
      chainId,
      feeSpeed
    )

    overallBatchMinGasPrice = await calculateGasPrice(transactionGasDetails)

    const broadcasterFeeERC20AmountRecipient: RailgunERC20AmountRecipient | undefined = undefined

    const progressCallback = (progress: number) => {
      if (RAILGUN_DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[Railgun unshield base] proof progress', progress)
      }
    }

    await invokeGenerateUnshieldBaseTokenProof(
      [
        TXIDVersion.V2_PoseidonMerkle,
        networkName,
        recipientAddress,
        railgunWalletID,
        encryptionKey,
        wrappedERC20Amount,
        broadcasterFeeERC20AmountRecipient,
        sendWithPublicWallet,
        overallBatchMinGasPrice,
        progressCallback
      ],
      progressCallback
    )
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('generateUnshieldBaseTokenProof failed', err)
    // Proof failed against a stale UTXO merkletree — rebuild it in the background
    // so the user's retry can prove (the mapped message tells them to wait+retry).
    if (isProvingMerkleFailure(err)) {
      // Unfreeze first: the rescan runs its own interlock and needs to mutate.
      releaseMerkletree()
      releaseMerkletree = () => {}
      void triggerFullUTXORescanForChain(chainId, railgunWalletID)
    }
    wrapUnshieldError(err)
  } finally {
    releaseMerkletree()
  }

  return {
    transactionGasDetails,
    overallBatchMinGasPrice
  }
}

export async function executePreparedUnshieldBaseTokenWithSDK(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  amount: bigint
  railgunWalletID: string
  encryptionKey: string
  recipientAddress: string
  gasDetails: PreparedUnshieldGasDetails
}): Promise<string> {
  const {
    mnemonic,
    chainId,
    amount,
    railgunWalletID,
    encryptionKey,
    recipientAddress,
    gasDetails
  } = params

  const networkName = getNetworkNameForChainId(chainId)
  if (!networkName) {
    throw new Error(`Unsupported chainId for Railgun unshield: ${chainId}`)
  }

  const rpcUrl = getRpcUrlForChainId(chainId)
  if (!rpcUrl) {
    throw new Error(`No RPC URL configured for chainId ${chainId}`)
  }

  const wrappedTokenAddress = getWrappedBaseTokenAddress(chainId)
  if (!wrappedTokenAddress) {
    throw new Error(`No wrapped base token configured for chainId ${chainId}`)
  }

  const provider = new JsonRpcProvider(rpcUrl)
  const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

  const sendWithPublicWallet = true

  const wrappedERC20Amount: RailgunERC20Amount = {
    tokenAddress: wrappedTokenAddress,
    amount
  }

  const broadcasterFeeERC20AmountRecipient: RailgunERC20AmountRecipient | undefined = undefined

  try {
    const { transaction } = await invokePopulateProvedUnshieldBaseToken([
      TXIDVersion.V2_PoseidonMerkle,
      networkName,
      recipientAddress,
      railgunWalletID,
      wrappedERC20Amount,
      broadcasterFeeERC20AmountRecipient,
      sendWithPublicWallet,
      gasDetails.overallBatchMinGasPrice,
      gasDetails.transactionGasDetails
    ])

    console.log('transaction', transaction)

    // Simulate transaction before broadcasting to catch invalid proofs early
    try {
      await provider.call({
        to: transaction.to,
        data: transaction.data,
        from: wallet.address,
        value: transaction.value ?? 0n
      })
    } catch (simErr) {
      // eslint-disable-next-line no-console
      console.error('[Railgun unshield base] simulation failed:', simErr)
      throw new Error(
        `Unshield base token transaction would revert on-chain. This usually means the ZK proof is invalid ` +
          `(stale Merkle tree or corrupted circuit artifacts). Please try again. ` +
          `Details: ${simErr instanceof Error ? simErr.message : String(simErr)}`
      )
    }

    const tx = await wallet.sendTransaction(transaction)
    await tx.wait()
    return tx.hash
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('populateProvedUnshieldBaseToken failed', err)
    wrapUnshieldError(err)
  }
}

export async function unshieldBaseTokenWithSDK(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  amount: bigint
  railgunWalletID: string
  encryptionKey: string
  recipientAddress: string
}): Promise<string> {
  const { mnemonic, chainId, amount, railgunWalletID, encryptionKey, recipientAddress } = params

  // eslint-disable-next-line no-console
  console.log('unshieldBaseTokenWithSDK', params)

  const gasDetails = await generateUnshieldProofBaseTokenWithSDK({
    mnemonic,
    passphrase: params.passphrase,
    chainId,
    amount,
    railgunWalletID,
    encryptionKey,
    recipientAddress
  })

  return executePreparedUnshieldBaseTokenWithSDK({
    mnemonic,
    passphrase: params.passphrase,
    chainId,
    amount,
    railgunWalletID,
    encryptionKey,
    recipientAddress,
    gasDetails
  })
}
